import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Loader2, UserPlus } from "lucide-react";
import { ManagedUserRole } from "@/services/users";
import { useCreateInvitation } from "@/hooks/queries";
import NoticeAlert from "@/components/common/NoticeAlert";
import TrialLocked from "@/components/trial/TrialLocked";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { companyRules, emailRules } from "./fieldRules";
import { useListPath } from "./listReturn";
import { describedBy, FieldError, showServerErrors } from "./formErrors";
import { USER_PAGES } from "./userPages";

interface InviteFormData {
  email: string;
  /** Brokers only. */
  company_name: string;
}

/** Invites a user of the page's type (a broker, underwriter, or admin support) into the
 * manager's organization, which the server picks: their default one. */
const UserInvite: React.FC<{ role: ManagedUserRole }> = ({ role }) => {
  const navigate = useNavigate();
  const { path, inviteTitle } = USER_PAGES[role];
  const listPath = useListPath(path);
  const isBroker = role === "broker";
  const createInvitation = useCreateInvitation();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormData>({
    defaultValues: {
      email: "",
      company_name: "",
    },
  });

  const onSubmit = async ({ email, company_name }: InviteFormData) => {
    setError(null);
    const normalizedEmail = email.trim().toLowerCase();
    try {
      await createInvitation.mutateAsync({
        email: normalizedEmail,
        role,
        company_name: isBroker ? company_name : undefined,
      });
      navigate(listPath, {
        state: { notice: { kind: "success", text: `Invitation sent to ${normalizedEmail}.` } },
      });
    } catch (err) {
      setError(showServerErrors(err, setFieldError, ["email", "company_name"]));
    }
  };

  return (
    <div className="mx-auto w-full max-w-lg">
      <NoticeAlert
        notice={error ? { kind: "error", text: error } : null}
        onDismiss={() => setError(null)}
      />

      <Card>
        <CardHeader className="flex-row items-center gap-3 space-y-0 p-5 sm:p-6">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UserPlus className="h-5 w-5" aria-hidden />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">{inviteTitle}</h1>
        </CardHeader>
        <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                autoComplete="off"
                {...register("email", emailRules)}
                {...describedBy("invite-email", errors.email)}
              />
              <FieldError id="invite-email" error={errors.email} />
            </div>

            {isBroker && (
              <div className="space-y-2">
                <Label htmlFor="invite-company">Company</Label>
                <Input
                  id="invite-company"
                  autoComplete="off"
                  {...register("company_name", companyRules)}
                  {...describedBy("invite-company", errors.company_name)}
                />
                <FieldError id="invite-company" error={errors.company_name} />
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => navigate(listPath)}
              >
                Cancel
              </Button>
              <TrialLocked lock="team" inline className="w-full sm:w-auto">
                <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send invitation
                </Button>
              </TrialLocked>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserInvite;
